import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import StaffQRGenerator from './components/StaffQRGenerator';
import AttendanceTracker from './components/AttendanceTracker';
import AttendanceHistory from './components/AttendanceHistory';
import QRScanner from './components/QRScanner';

const StaffAttendanceManagementSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, loading: authLoading, profileLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tracker');
  const [staffMembers, setStaffMembers] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [stats, setStats] = useState({
    currentlyPresent: 0,
    onLunchBreak: 0,
    totalCheckIns: 0,
    averageHours: 0
  });

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (!authLoading && !profileLoading && (!user || !isStaffMember)) {
      navigate('/member-dashboard');
    }
  }, [user, isStaffMember, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isStaffMember && !profileLoading) {
      loadData();
    }
  }, [currentTenant, isStaffMember, profileLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStaffMembers(),
        loadAttendanceRecords(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStaffMembers = async () => {
    try {
      const { data, error } = await supabase?.from('tenant_members')?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email, avatar_url),
          staff_qr_codes(id, qr_code_data, is_active, expires_at)
        `)?.eq('tenant_id', currentTenant?.id)?.in('role', ['gym_owner', 'staff', 'trainer'])?.eq('is_active', true)?.order('user_profiles(full_name)');

      if (error) throw error;
      setStaffMembers(data || []);
    } catch (error) {
      console.error('Error loading staff members:', error);
    }
  };

  const loadAttendanceRecords = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data, error } = await supabase?.from('staff_attendance')?.select(`
          *,
          user_profiles!staff_attendance_user_id_fkey(full_name, email, avatar_url)
        `)?.eq('tenant_id', currentTenant?.id)?.gte('check_in_time', today?.toISOString())?.order('check_in_time', { ascending: false });

      if (error) throw error;
      setAttendanceRecords(data || []);
    } catch (error) {
      console.error('Error loading attendance records:', error);
    }
  };

  const loadStats = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data, error } = await supabase?.from('staff_attendance')?.select('*')?.eq('tenant_id', currentTenant?.id)?.gte('check_in_time', today?.toISOString());

      if (error) throw error;

      const currentlyPresent = data?.filter(r => r?.check_in_time && !r?.check_out_time)?.length || 0;
      const onLunchBreak = data?.filter(r => r?.lunch_start_time && !r?.lunch_end_time)?.length || 0;
      
      const completedShifts = data?.filter(r => r?.total_hours) || [];
      const averageHours = completedShifts?.length > 0
        ? completedShifts?.reduce((sum, r) => sum + (r?.total_hours || 0), 0) / completedShifts?.length
        : 0;

      setStats({
        currentlyPresent,
        onLunchBreak,
        totalCheckIns: data?.length || 0,
        averageHours: averageHours?.toFixed(1)
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleAttendanceUpdate = () => {
    loadData();
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="UserCheck" size={32} className="text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Staff Attendance Management</h1>
          </div>
          <p className="text-muted-foreground">QR code-based staff attendance tracking with check-in, lunch break, and check-out</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Users" size={20} className="text-green-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.currentlyPresent}</span>
            </div>
            <p className="text-sm text-muted-foreground">Currently Present</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Coffee" size={20} className="text-orange-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.onLunchBreak}</span>
            </div>
            <p className="text-sm text-muted-foreground">On Lunch Break</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="LogIn" size={20} className="text-blue-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.totalCheckIns}</span>
            </div>
            <p className="text-sm text-muted-foreground">Today's Check-Ins</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Clock" size={20} className="text-purple-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.averageHours}h</span>
            </div>
            <p className="text-sm text-muted-foreground">Average Hours</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('tracker')}
            variant={activeTab === 'tracker' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="ScanLine" size={18} />
            Attendance Tracker
          </Button>
          <Button
            onClick={() => setActiveTab('qr-generator')}
            variant={activeTab === 'qr-generator' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="QrCode" size={18} />
            QR Code Generator
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            variant={activeTab === 'history' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="History" size={18} />
            Attendance History
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'tracker' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <QRScanner onScanComplete={handleAttendanceUpdate} />
            <AttendanceTracker 
              attendanceRecords={attendanceRecords}
              onUpdate={handleAttendanceUpdate}
            />
          </div>
        )}

        {activeTab === 'qr-generator' && (
          <StaffQRGenerator 
            staffMembers={staffMembers}
            onQRGenerated={loadStaffMembers}
          />
        )}

        {activeTab === 'history' && (
          <AttendanceHistory 
            attendanceRecords={attendanceRecords}
            onRefresh={loadAttendanceRecords}
          />
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default StaffAttendanceManagementSystem;